import React, { useState, useEffect } from 'react';
import {


  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  StatusBar,
} from 'react-native';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Bulletproof icon imports with fallbacks
const FallbackIcon = ({ name, size = 24, color = '#666', style, ...props }) => (
  <View 
    style={[
      {
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: size / 2,
        opacity: 0.3
      },
      style
    ]}
    {...props}
  />
);

let Ionicons = FallbackIcon;
let FontAwesome5 = FallbackIcon;

try {
  const iconsModule = require('@expo/vector-icons');
  if (iconsModule) {
    if (iconsModule.Ionicons && typeof iconsModule.Ionicons === 'function') {
      Ionicons = iconsModule.Ionicons;
    }
    if (iconsModule.FontAwesome5 && typeof iconsModule.FontAwesome5 === 'function') {
      FontAwesome5 = iconsModule.FontAwesome5;
    }
  }
} catch (error) {
  console.warn('@expo/vector-icons not available, using fallback');
}

// Mock AsyncStorage for environments where it's not available
let AsyncStorage;
try {
  AsyncStorage = require('@react-native-async-storage/async-storage').default;
} catch (error) {
  console.warn('AsyncStorage not available, using mock implementation');
  AsyncStorage = {
    getItem: async (key) => null,
    setItem: async (key, value) => {},
    removeItem: async (key) => {}
  };
}

// Bulletproof service imports with fallbacks - NO DUPLICATE DECLARATIONS
class MockExperienceService {
  async initialize() { return true; }
  
  async fetchExperience(id) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        id,
        title: 'Senior Software Developer',
        company: 'Tech Innovation Corp',
        location: 'San Francisco, CA',
        startDate: '01/2021',
        endDate: '12/2023',
        isCurrentJob: false,
        employmentType: 'Full-time',
        description: 'Led development of scalable web applications using React, Node.js, and cloud technologies. Managed a team of 3 developers and collaborated with cross-functional teams to deliver high-quality software solutions.',
        achievements: [
          'Increased application performance by 40%',
          'Led migration to microservices architecture',
          'Mentored 5 junior developers'
        ],
        skills: ['JavaScript', 'React', 'Node.js', 'AWS', 'Team Leadership']
      });
    }, 500));
  }
  
  async updateExperience(userId, experienceData) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        success: true,
        message: 'Experience updated successfully'
      });
    }, 1000));
  }
  
  async deleteExperience(userId, experienceId) {
    return new Promise(resolve => setTimeout(() => {
      resolve({
        success: true,
        message: 'Experience deleted successfully'
      });
    }, 1000));
  }
  
  validateExperienceData(data) {
    const errors = {};
    if (!data.title || data.title.trim() === '') {
      errors.title = 'Job title is required';
    }
    if (!data.company || data.company.trim() === '') {
      errors.company = 'Company name is required';
    }
    if (!data.startDate || data.startDate.trim() === '') {
      errors.startDate = 'Start date is required';
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  }
}

class MockUnifiedService {
  async initialize() { return true; }
  async trackUserActivity() { return { success: true }; }
}

class MockDashboardService {
  async initialize() { return true; }
  async updateDashboardMetrics() { return { success: true }; }
}

// Import services with fallbacks - SINGLE DECLARATION
let ExperienceService = MockExperienceService;
let UnifiedService = MockUnifiedService;
let DashboardService = MockDashboardService;

try {
  const experienceModule = require('../../services/ExperienceManagementService');
  if (experienceModule && experienceModule.default) {
    ExperienceService = experienceModule.default;
  } else if (experienceModule && typeof experienceModule === 'function') {
    ExperienceService = experienceModule;
  }
} catch (error) {
  console.warn('ExperienceManagementService not available, using mock');
}

try {
  const unifiedModule = require('../../services/UnifiedIntegrationService');
  if (unifiedModule && unifiedModule.default) {
    UnifiedService = unifiedModule.default;
  } else if (unifiedModule && typeof unifiedModule === 'function') {
    UnifiedService = unifiedModule;
  }
} catch (error) {
  console.warn('UnifiedIntegrationService not available, using mock');
}

try {
  const dashboardModule = require('../../services/DashboardIntegrationService');
  if (dashboardModule && dashboardModule.default) {
    DashboardService = dashboardModule.default;
  } else if (dashboardModule && typeof dashboardModule === 'function') {
    DashboardService = dashboardModule;
  }
} catch (error) {
  console.warn('DashboardIntegrationService not available, using mock');
}

const { width, height } = Dimensions.get('window');

const EditExperience = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Get experience data from navigation params
  const experienceData = route.params?.experience || {};
  const isEditing = !!experienceData.id;

  // Services - USING RENAMED VARIABLES
  const [experienceService] = useState(() => new ExperienceService());
  const [unifiedService] = useState(() => new UnifiedService());
  const [dashboardService] = useState(() => new DashboardService());

  // State management
  const [experience, setExperience] = useState({
    id: experienceData.id || null,
    title: experienceData.title || '',
    company: experienceData.company || '',
    location: experienceData.location || '',
    startDate: experienceData.startDate || '',
    endDate: experienceData.endDate || '',
    isCurrentJob: experienceData.isCurrentJob || false,
    employmentType: experienceData.employmentType || 'Full-time',
    description: experienceData.description || '',
    achievements: experienceData.achievements || [''],
    skills: experienceData.skills || [''],
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Employment type options
  const employmentTypes = [
    'Full-time',
    'Part-time',
    'Contract',
    'Freelance',
    'Internship',
    'Temporary',
    'Volunteer'
  ];

  useEffect(() => {
    initializeServices();
    if (isEditing && experienceData.id) {
      loadExperienceData();
    }
  }, []);

  useEffect(() => {
    // Check if there are changes compared to original data
    const hasExperienceChanges = JSON.stringify(experience) !== JSON.stringify({
      id: experienceData.id || null,
      title: experienceData.title || '',
      company: experienceData.company || '',
      location: experienceData.location || '',
      startDate: experienceData.startDate || '',
      endDate: experienceData.endDate || '',
      isCurrentJob: experienceData.isCurrentJob || false,
      employmentType: experienceData.employmentType || 'Full-time',
      description: experienceData.description || '',
      achievements: experienceData.achievements || [''],
      skills: experienceData.skills || [''],
    });
    setHasChanges(hasExperienceChanges);
  }, [experience, experienceData]);

  const initializeServices = async () => {
    try {
      await Promise.all([
        experienceService.initialize().catch(e => console.warn('Experience service init failed:', e)),
        unifiedService.initialize().catch(e => console.warn('Unified service init failed:', e)),
        dashboardService.initialize().catch(e => console.warn('Dashboard service init failed:', e))
      ]);

      // Track screen view
      try {
        await unifiedService.trackUserActivity(isEditing ? 'edit_experience_viewed' : 'add_experience_viewed');
      } catch (error) {
        console.warn('Failed to track activity:', error);
      }
    } catch (error) {
      console.error('Error initializing services:', error);
    }
  };

  const loadExperienceData = async () => {
    if (!isEditing) return;

    setLoading(true);
    try {
      const result = await experienceService.fetchExperience(experienceData.id);
      if (result) {
        setExperience(result);
      }
    } catch (error) {
      console.error('Error loading experience data:', error);
      Alert.alert('Error', 'Failed to load experience data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const validateInput = () => {
    const validation = experienceService.validateExperienceData(experience);
    setErrors(validation.errors);
    return validation.isValid;
  };

  const handleSaveExperience = async () => {
    console.log('Save experience initiated...');
    
    if (!validateInput()) {
      Alert.alert('Validation Error', 'Please correct the errors in the form.');
      return;
    }

    setSaving(true);
    
    try {
      const userId = 'demo_user'; // In real app, get from auth context
      const result = await experienceService.updateExperience(userId, experience);
      
      if (result.success) {
        // Save to cache
        const cacheKey = isEditing ? 'editedExperience' : 'newExperience';
        await AsyncStorage.setItem(cacheKey, JSON.stringify(experience));
        
        // Track activity
        try {
          await unifiedService.trackUserActivity(isEditing ? 'experience_updated' : 'experience_added');
          await dashboardService.updateDashboardMetrics('experience_updated', { userId });
        } catch (error) {
          console.warn('Failed to track activity:', error);
        }
        
        // Show success message and navigate back
        Alert.alert(
          'Success',
          result.message || `Experience ${isEditing ? 'updated' : 'added'} successfully!`,
          [
            {
              text: 'OK',
              onPress: () => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('Profile');
                }
              }
            }
          ]
        );
      } else {
        throw new Error(result.error || 'Failed to save experience');
      }
      
    } catch (error) {
      console.error('Error saving experience:', error);
      Alert.alert('Error', error.message || 'Failed to save experience. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteExperience = async () => {
    if (!isEditing) return;

    Alert.alert(
      'Delete Experience',
      'Are you sure you want to delete this work experience?',
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Delete', 
          style: 'destructive',
          onPress: async () => {
            setSaving(true);
            try {
              const userId = 'demo_user';
              const result = await experienceService.deleteExperience(userId, experience.id);
              
              if (result.success) {
                // Track activity
                try {
                  await unifiedService.trackUserActivity('experience_deleted');
                  await dashboardService.updateDashboardMetrics('experience_deleted', { userId });
                } catch (error) {
                  console.warn('Failed to track activity:', error);
                }
                
                Alert.alert('Success', 'Experience deleted successfully!', [
                  { text: 'OK', onPress: () => navigation.goBack() }
                ]);
              } else {
                throw new Error(result.error || 'Failed to delete experience');
              }
            } catch (error) {
              console.error('Error deleting experience:', error);
              Alert.alert('Error', 'Failed to delete experience. Please try again.');
            } finally {
              setSaving(false);
            }
          }
        }
      ]
    );
  };

  const handleBackPress = () => {
    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'Are you sure you want to discard your changes?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          { 
            text: 'Discard', 
            style: 'destructive', 
            onPress: () => {
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                navigation.navigate('Profile');
              }
            }
          }
        ]
      );
    } else {
      if (navigation.canGoBack()) {
        navigation.goBack();
      } else {
        navigation.navigate('Profile');
      }
    }
  };

  const updateExperience = (field, value) => {
    setExperience({ ...experience, [field]: value });
  };

  const addAchievement = () => {
    setExperience({
      ...experience,
      achievements: [...experience.achievements, '']
    });
  };

  const updateAchievement = (index, value) => {
    const newAchievements = [...experience.achievements];
    newAchievements[index] = value;
    setExperience({ ...experience, achievements: newAchievements });
  };

  const removeAchievement = (index) => {
    if (experience.achievements.length > 1) {
      const newAchievements = experience.achievements.filter((_, i) => i !== index);
      setExperience({ ...experience, achievements: newAchievements });
    }
  };

  const addSkill = () => {
    setExperience({
      ...experience,
      skills: [...experience.skills, '']
    });
  };

  const updateSkill = (index, value) => {
    const newSkills = [...experience.skills];
    newSkills[index] = value;
    setExperience({ ...experience, skills: newSkills });
  };

  const removeSkill = (index) => {
    if (experience.skills.length > 1) {
      const newSkills = experience.skills.filter((_, i) => i !== index);
      setExperience({ ...experience, skills: newSkills });
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <StatusBar barStyle="dark-content" backgroundColor="#fff" />
        <ActivityIndicator size={36} color="#4CAF50" />
        <Text style={styles.loadingText}>Loading Experience...</Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView 
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <StatusBar barStyle="dark-content" backgroundColor="#fff" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleBackPress}>
          <Ionicons name="arrow-back" size={24} color="#333" />
        </TouchableOpacity>
        
        <Text style={styles.headerTitle}>
          {isEditing ? 'Edit Experience' : 'Add Experience'}
        </Text>
        
        <View style={styles.headerActions}>
          {isEditing && (
            <TouchableOpacity 
              style={styles.deleteButton}
              onPress={handleDeleteExperience}
              disabled={saving}
            >
              <Ionicons name="trash-outline" size={20} color="#f44336" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Job Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Job Information</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Job Title *</Text>
            <TextInput
              style={[styles.input, errors.title && styles.inputError]}
              value={experience.title}
              onChangeText={(text) => updateExperience('title', text)}
              placeholder="e.g., Senior Software Developer"
              autoCapitalize="words"
            />
            {errors.title && <Text style={styles.errorText}>{errors.title}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Company *</Text>
            <TextInput
              style={[styles.input, errors.company && styles.inputError]}
              value={experience.company}
              onChangeText={(text) => updateExperience('company', text)}
              placeholder="e.g., Tech Innovation Corp"
              autoCapitalize="words"
            />
            {errors.company && <Text style={styles.errorText}>{errors.company}</Text>}
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Location</Text>
            <TextInput
              style={styles.input}
              value={experience.location}
              onChangeText={(text) => updateExperience('location', text)}
              placeholder="e.g., San Francisco, CA"
              autoCapitalize="words"
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Employment Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.employmentTypeScroll}>
              {employmentTypes.map((type) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.employmentTypeChip,
                    experience.employmentType === type && styles.employmentTypeChipSelected
                  ]}
                  onPress={() => updateExperience('employmentType', type)}
                >
                  <Text style={[
                    styles.employmentTypeChipText,
                    experience.employmentType === type && styles.employmentTypeChipTextSelected
                  ]}>
                    {type}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>

        {/* Duration */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Duration</Text>
          
          <View style={styles.dateRow}>
            <View style={styles.dateContainer}>
              <Text style={styles.label}>Start Date *</Text>
              <TextInput
                style={[styles.input, errors.startDate && styles.inputError]}
                value={experience.startDate}
                onChangeText={(text) => updateExperience('startDate', text)}
                placeholder="MM/YYYY"
              />
              {errors.startDate && <Text style={styles.errorText}>{errors.startDate}</Text>}
            </View>
            
            <View style={styles.dateContainer}>
              <Text style={styles.label}>End Date</Text>
              <TextInput
                style={[styles.input, experience.isCurrentJob && styles.inputDisabled]}
                value={experience.isCurrentJob ? 'Present' : experience.endDate}
                onChangeText={(text) => updateExperience('endDate', text)}
                placeholder="MM/YYYY"
                editable={!experience.isCurrentJob}
              />
            </View>
          </View>

          <TouchableOpacity 
            style={styles.checkboxContainer}
            onPress={() => updateExperience('isCurrentJob', !experience.isCurrentJob)}
          >
            <View style={[styles.checkbox, experience.isCurrentJob && styles.checkboxChecked]}>
              {experience.isCurrentJob && (
                <Ionicons name="checkmark" size={16} color="#fff" />
              )}
            </View>
            <Text style={styles.checkboxLabel}>I currently work here</Text>
          </TouchableOpacity>
        </View>

        {/* Description */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Job Description</Text>
          
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Description</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={experience.description}
              onChangeText={(text) => updateExperience('description', text)}
              placeholder="Describe your role, responsibilities, and key contributions..."
              multiline
              numberOfLines={5}
              textAlignVertical="top"
            />
          </View>
        </View>

        {/* Key Achievements */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Key Achievements</Text>
          
          {experience.achievements.map((achievement, index) => (
            <View key={index} style={styles.dynamicInputContainer}>
              <TextInput
                style={styles.input}
                value={achievement}
                onChangeText={(text) => updateAchievement(index, text)}
                placeholder={`Achievement ${index + 1}`}
                multiline
              />
              <View style={styles.dynamicInputActions}>
                {experience.achievements.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeAchievement(index)}
                  >
                    <Ionicons name="remove-circle" size={20} color="#f44336" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          
          <TouchableOpacity style={styles.addButton} onPress={addAchievement}>
            <Ionicons name="add-circle" size={20} color="#4CAF50" />
            <Text style={styles.addButtonText}>Add Achievement</Text>
          </TouchableOpacity>
        </View>

        {/* Skills Used */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Skills Used</Text>
          
          {experience.skills.map((skill, index) => (
            <View key={index} style={styles.dynamicInputContainer}>
              <TextInput
                style={styles.input}
                value={skill}
                onChangeText={(text) => updateSkill(index, text)}
                placeholder={`Skill ${index + 1}`}
                autoCapitalize="words"
              />
              <View style={styles.dynamicInputActions}>
                {experience.skills.length > 1 && (
                  <TouchableOpacity
                    style={styles.removeButton}
                    onPress={() => removeSkill(index)}
                  >
                    <Ionicons name="remove-circle" size={20} color="#f44336" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          ))}
          
          <TouchableOpacity style={styles.addButton} onPress={addSkill}>
            <Ionicons name="add-circle" size={20} color="#4CAF50" />
            <Text style={styles.addButtonText}>Add Skill</Text>
          </TouchableOpacity>
        </View>

        {/* Save Button */}
        <TouchableOpacity
          style={[styles.saveButton, (!hasChanges || saving) && styles.saveButtonDisabled]}
          onPress={handleSaveExperience}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.saveButtonText}>
              {isEditing ? 'Update Experience' : 'Add Experience'}
            </Text>
          )}
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    paddingTop: 50,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  deleteButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#ffebee',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 20,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fff',
  },
  inputError: {
    borderColor: '#f44336',
    backgroundColor: '#ffebee',
  },
  inputDisabled: {
    backgroundColor: '#f5f5f5',
    color: '#999',
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  errorText: {
    color: '#f44336',
    fontSize: 14,
    marginTop: 4,
  },
  dateRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  dateContainer: {
    flex: 1,
    marginRight: 10,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#ddd',
    borderRadius: 4,
    marginRight: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  checkboxLabel: {
    fontSize: 16,
    color: '#333',
  },
  employmentTypeScroll: {
    flexDirection: 'row',
  },
  employmentTypeChip: {
    backgroundColor: '#f0f0f0',
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 10,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  employmentTypeChipSelected: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  employmentTypeChipText: {
    fontSize: 14,
    color: '#666',
  },
  employmentTypeChipTextSelected: {
    color: '#fff',
    fontWeight: '600',
  },
  dynamicInputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  dynamicInputActions: {
    marginLeft: 12,
    justifyContent: 'center',
  },
  removeButton: {
    padding: 4,
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    borderStyle: 'dashed',
  },
  addButtonText: {
    fontSize: 16,
    color: '#4CAF50',
    fontWeight: '600',
    marginLeft: 8,
  },
  saveButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
    marginTop: 20,
    marginBottom: 40,
  },
  saveButtonDisabled: {
    backgroundColor: '#ccc',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
  },
});

export default EditExperience;
